<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import { useScreen } from "@/composable/useResponsive";
import type { DRBranchWithContributors } from "@/types";

const props = defineProps<{
  modelValue: boolean;
  branch: DRBranchWithContributors;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const { t } = useTranslation();

const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const { screen } = useScreen();

const forkedFromInfo = computed(() => {
  if (!props.branch.forkedFromBranch) return null;
  return {
    name: props.branch.forkedFromBranch.name,
    date: new Date(props.branch.createdAt).toLocaleDateString(),
  };
});

const close = () => {
  showModal.value = false;
};
</script>

<template>
  <el-dialog
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    v-model="showModal"
    :title="$t('dashboard:branches.infoDialog.title')"
    append-to-body
    :width="screen.gt.sm ? '550px' : '100%'"
  >
    <div class="branch-info-content">
      <section>
        <div class="forked-from-info mt-4">
          <el-icon class="me-2" size="2rem"
            ><i class="bi-diagram-2"></i
          ></el-icon>
          <span v-if="forkedFromInfo">{{
            t("dashboard:branches.infoDialog.forkedFrom", {
              name: forkedFromInfo.name,
              date: forkedFromInfo.date,
            })
          }}</span>
          <span v-else>{{
            $t("dashboard:branches.infoDialog.initialBranch")
          }}</span>
        </div>
      </section>
      <section class="mt-4">
        <h4 class="section-title mb-3">
          {{ $t("dashboard:branches.infoDialog.whatIsVersion") }}
        </h4>
        <ul class="explanation-list">
          <li>{{ $t("dashboard:branches.infoDialog.explanation1") }}</li>
          <li>{{ $t("dashboard:branches.infoDialog.explanation2") }}</li>
          <li>{{ $t("dashboard:branches.infoDialog.explanation3") }}</li>
        </ul>
      </section>
    </div>

    <template #footer>
      <div class="dialog-footer d-flex justify-content-end">
        <DRBtn :label="$t('common:close')" variant="outline" @click="close" />
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.branch-info-content {
  .section-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--el-text-color-primary);
  }

  .explanation-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      position: relative;
      padding-left: 1.5rem;
      margin-bottom: 0.75rem;
      line-height: 1.5;
      color: var(--el-text-color-regular);

      &::before {
        content: "•";
        position: absolute;
        left: 0.5rem;
        color: var(--el-color-primary);
        font-weight: bold;
      }
    }
  }

  .info-grid {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .info-row {
    display: flex;
    align-items: center;
    gap: 1rem;

    .info-label {
      min-width: 100px;
      color: var(--el-text-color-secondary);
      font-size: 0.9rem;
    }

    .info-value {
      color: var(--el-text-color-primary);
      font-weight: 500;
    }
  }

  .forked-from-info {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    background-color: var(--el-fill-color-light);
    border-radius: 8px;
    color: var(--el-text-color-regular);
    font-size: 0.9rem;

    i {
      color: var(--el-color-primary);
    }
  }
}
</style>
